import React from 'react';
import { BookOpen, Gauge, KeyRound, PieChart } from 'lucide-react';
import { useGovernedMcp } from '../governedMcpStore';
import type { UsageSection } from '../governedMcpTypes';
import ApiKeysTab from './ApiKeysTab';
import QuotaGuideTab from './QuotaGuideTab';
import QuotasSpendingsTab from './QuotasSpendingsTab';
import UsageStatisticsTab from './UsageStatisticsTab';

const SECTIONS: { id: UsageSection; label: string; hint: string; icon: React.ComponentType<{ size?: number; className?: string }> }[] = [
  { id: 'statistics', label: 'Statistics', hint: 'Overall spend', icon: PieChart },
  { id: 'api-keys', label: 'API Keys', hint: 'Gateway credentials', icon: KeyRound },
  { id: 'quotas', label: 'Quotas & Spendings', hint: 'Client, tool, and gateway limits', icon: Gauge },
  { id: 'guide', label: 'Guide', hint: 'How quotas work', icon: BookOpen },
];

export default function AiUsageLimitsTab({
  isLight,
  onToast,
}: {
  isLight: boolean;
  onToast: (type: 'success' | 'error' | 'info', message: string) => void;
}) {
  const { state, setUsageSection } = useGovernedMcp();
  const section = state.usageSection;

  return (
    <div className="flex gap-5 items-start">
      <nav
        className={`w-52 shrink-0 rounded-xl border p-2 ${isLight ? 'bg-white border-neutral-200' : 'bg-neutral-900 border-neutral-800'}`}
        aria-label="AI usage sections"
      >
        {SECTIONS.map((item) => {
          const Icon = item.icon;
          const active = section === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => setUsageSection(item.id)}
              className={`w-full flex items-start gap-2.5 rounded-lg px-3 py-2.5 text-left transition-colors ${
                active
                  ? isLight
                    ? 'bg-indigo-50 text-indigo-700'
                    : 'bg-indigo-500/15 text-indigo-300'
                  : isLight
                    ? 'text-neutral-600 hover:bg-neutral-50'
                    : 'text-neutral-400 hover:bg-neutral-900'
              }`}
            >
              <Icon size={15} className="mt-0.5 shrink-0" />
              <span>
                <span className={`block text-sm font-medium ${active ? '' : isLight ? 'text-neutral-800' : 'text-neutral-200'}`}>
                  {item.label}
                </span>
                <span
                  className={`block text-[11px] mt-0.5 ${
                    active ? (isLight ? 'text-indigo-500' : 'text-indigo-400/80') : isLight ? 'text-neutral-400' : 'text-neutral-500'
                  }`}
                >
                  {item.hint}
                </span>
              </span>
            </button>
          );
        })}
      </nav>
      <div className="flex-1 min-w-0">
        {section === 'statistics' ? <UsageStatisticsTab isLight={isLight} /> : null}
        {section === 'api-keys' ? <ApiKeysTab isLight={isLight} onToast={onToast} /> : null}
        {section === 'quotas' ? <QuotasSpendingsTab isLight={isLight} onToast={onToast} /> : null}
        {section === 'guide' ? <QuotaGuideTab isLight={isLight} /> : null}
      </div>
    </div>
  );
}
